import { useCallback, useEffect, useRef, useState } from 'react'
import { Bell, Check, Globe, Loader2 } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { useOnlineBookingInbox } from '@/hooks/useOnlineBookingInbox'
import { fetchOnlineBookingQueue } from '@/lib/api/publicBooking'
import { formatArrivalTime } from '@/lib/calendar/checkIn'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { isOnlineBookingUnread } from '@/lib/onlineBookingInbox'
import { hasPermission, PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'

export function HeaderNotificationsPanel() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { activeBranchId } = useBranch()
  const { t } = useUiLocale()
  const canManageAppointments = hasPermission(user, PERMISSIONS.APPOINTMENTS_MANAGE)
  const {
    unreadCount,
    pendingItems,
    refresh: refreshInbox,
    markBookingSeen,
    markAllSeen,
  } = useOnlineBookingInbox()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  /** @type {[Array<import('@/types/api').Appointment> | null, import('react').Dispatch<import('react').SetStateAction<Array<import('@/types/api').Appointment> | null>>]} */
  const [appointments, setAppointments] = useState(null)
  const panelRef = useRef(null)

  const loadNotifications = useCallback(async () => {
    if (!canManageAppointments) {
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchOnlineBookingQueue('pending')
      setAppointments(data.appointments ?? [])
      await refreshInbox()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load notifications.'))
      setAppointments([])
    } finally {
      setLoading(false)
    }
  }, [canManageAppointments, refreshInbox])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    loadNotifications()

    function handleClickOutside(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [loadNotifications, open])

  function openBooking(appointmentId) {
    markBookingSeen(appointmentId)
    setOpen(false)
    navigate(`/online-bookings?booking=${appointmentId}`)
  }

  if (!canManageAppointments) {
    return (
      <button
        type="button"
        className="relative flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
        aria-label={t('common.notifications')}
      >
        <Bell className="h-4 w-4" />
      </button>
    )
  }

  const items = appointments ?? []

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={
          unreadCount > 0
            ? `${t('notifications.title')} — ${unreadCount} ${t('notifications.unreadLabel')}`
            : t('notifications.title')
        }
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className={cn(
            'absolute right-0 top-[calc(100%+0.5rem)] z-50 w-[min(100vw-2rem,360px)]',
            'overflow-hidden rounded-2xl border border-border/60 bg-card shadow-lg',
          )}
        >
          <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-foreground">{t('notifications.title')}</p>
              {unreadCount > 0 ? (
                <p className="text-xs text-amber-700">
                  {unreadCount} {t('notifications.newCountLabel')}
                </p>
              ) : null}
            </div>
            {unreadCount > 0 ? (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                {t('notifications.new')}
              </span>
            ) : null}
          </div>

          <div className="max-h-[min(60vh,320px)] overflow-y-auto">
            {loading ? (
              <div className="flex items-center gap-2 px-4 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('notifications.loading')}
              </div>
            ) : error ? (
              <p className="px-4 py-6 text-sm text-red-600">{error}</p>
            ) : items.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Check className="mx-auto h-8 w-8 text-emerald-500/80" />
                <p className="mt-2 text-sm font-medium text-foreground">{t('notifications.empty')}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t('notifications.emptyHint')}</p>
              </div>
            ) : (
              <ul className="divide-y divide-border/60">
                {items.slice(0, 8).map((appointment) => {
                  const isUnread = isOnlineBookingUnread(appointment.id, pendingItems, activeBranchId)

                  return (
                    <li key={appointment.id}>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
                          isUnread && 'bg-amber-50/50',
                        )}
                        onClick={() => openBooking(appointment.id)}
                      >
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                          <Globe className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {appointment.customer?.name ?? t('notifications.guestFallback')}
                          </p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {t('notifications.onlineBooking')}
                          </p>
                          <p className="mt-1 text-xs font-medium text-foreground">
                            {formatArrivalTime(appointment.starts_at)}
                          </p>
                        </span>
                        {isUnread ? (
                          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                            {t('notifications.new')}
                          </span>
                        ) : (
                          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {t('notifications.needsApproval')}
                          </span>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="border-t border-border/60 bg-muted/20 px-4 py-2.5">
            <Link
              to="/online-bookings"
              className="block text-center text-sm font-medium text-primary hover:underline"
              onClick={() => {
                markAllSeen()
                setOpen(false)
              }}
            >
              {t('notifications.viewAll')}
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  )
}
